import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { StudioSubscriptionType } from '@/types'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { SalesFunnel } from '@/components/SalesFunnel'
// Импортируем хуки
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useMultiStudioAnalytics } from '@/hooks/useMultiStudioAnalytics'
import { useAnalyticsMetrics, type WeekMetrics } from '@/hooks/useAnalyticsMetrics'
import { useChartData } from '@/hooks/useChartData'
import { useMetricWeeks } from '@/hooks/useMetricWeeks'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { useSalesMixAnalytics } from '@/hooks/useSalesMixAnalytics'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useAnalyticsChartsState } from '@/hooks/useAnalyticsChartsState'
import { useGlobalWeeklyPlanDiagnostics } from '@/hooks/useGlobalWeeklyPlanDiagnostics'
import { useCrmRevenueComparison } from '@/hooks/useCrmRevenueComparison'
import { useMultiStudioFactorAnalysis } from '@/hooks/useMultiStudioFactorAnalysis'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import type { StudioCityRef } from '@/lib/partnerFeatures'
import {
  calculateClientRetention,
  resolveRangeEndMetricValue,
  resolveRangeGrowthStartMetricValue,
  resolveRangeStartMetricValue
} from '@/lib/analytics/clientMetrics'
// Импортируем компоненты аналитики
import { 
  PlanVsFactSection, 
  PeriodSelector, 
  DailyPeriodMetricsTable,
  AnalyticsDashboardLayout,
  AnalyticsKpiGrid,
  AnalyticsChartsSection,
  CrmComparisonModal
} from '@/components/analytics'

interface GlobalAnalyticsPageProps {
  cityId?: number | null
}

export function GlobalAnalyticsPage({ cityId }: GlobalAnalyticsPageProps = {}) {
  const { partnerNumber } = useParams<{ partnerNumber?: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { studios, selectedPartnerNumber, getPartnerStudios, setStudios } = useStudioStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const reloadAttemptsRef = useRef<Set<string>>(new Set())

  const activePartnerNumber = useMemo(() => {
    if (partnerNumber !== undefined) {
      const parsed = Number(partnerNumber)
      return Number.isNaN(parsed) ? null : parsed
    }
    return selectedPartnerNumber
  }, [partnerNumber, selectedPartnerNumber])
  const [isReloadingPartnerStudios, setIsReloadingPartnerStudios] = useState(false)

  const scopedStudios = useMemo(() => {
    const normalizePartnerNumber = (value: unknown): number | null => {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const parsed = Number(value)
      return Number.isNaN(parsed) ? null : parsed
    }

    const fromStore = getPartnerStudios(activePartnerNumber)
    if (activePartnerNumber === null || fromStore.length > 0) {
      return fromStore
    }

    const directMatch = studios.filter(
      (studio) => normalizePartnerNumber(studio.partner_number) === activePartnerNumber
    )
    if (directMatch.length > 0) {
      return directMatch
    }

    const allStudiosHaveNoPartner = studios.length > 0 && studios.every(
      (studio) => normalizePartnerNumber(studio.partner_number) === null
    )
    if (allStudiosHaveNoPartner) {
      return studios
    }

    return fromStore
  }, [getPartnerStudios, activePartnerNumber, studios])

  useEffect(() => {
    const reloadStudiosForPartnerContext = async () => {
      if (!user?.id || activePartnerNumber === null) {
        setIsReloadingPartnerStudios(false)
        return
      }
      if (scopedStudios.length > 0) {
        setIsReloadingPartnerStudios(false)
        return
      }

      const attemptKey = `${user.id}:${activePartnerNumber}`
      if (reloadAttemptsRef.current.has(attemptKey)) {
        setIsReloadingPartnerStudios(false)
        return
      }
      reloadAttemptsRef.current.add(attemptKey)

      try {
        if (isActive) {
          setIsReloadingPartnerStudios(true)
        }
        const normalizePartnerNumber = (value: unknown): number | null => {
          if (value === null || value === undefined || value === '') {
            return null
          }
          const parsed = Number(value)
          return Number.isNaN(parsed) ? null : parsed
        }

        const { data, error } = await withAuthRecovery(() =>
          (supabase as any).rpc('get_my_studios')
        )

        if (error) {
          console.error('❌ GlobalAnalyticsPage: failed to reload studios for partner context:', error)
          return
        }

        const processed = ((data || []) as any[]).map((studio: any) => {
          let role: StudioSubscriptionType | string = studio.team_role || 'manager'
          if (studio.access_kind === 'management_company' || studio.team_role === 'management_company') {
            role = 'management_company'
          } else if (studio.access_kind === 'owner') {
            role = 'owner'
          }

          return {
            studio_internal_id: studio.studio_internal_id,
            studio_number: studio.studio_number || `${studio.studio_internal_id}`,
            currency_code: studio.currency_code || 'RUB',
            city_id: studio.city_id || 0,
            address: studio.address,
            created_at: studio.created_at,
            updated_at: studio.updated_at,
            status_code: studio.status_code,
            partner_number: normalizePartnerNumber(studio.partner_number),
            access_kind: studio.access_kind,
            role,
            city: studio.city_name ? {
              city_id: studio.city_id,
              city_name: studio.city_name,
              population_thousands: null,
              city_population_group: null,
              city_land_group: null,
              country: 'Россия'
            } : undefined
          }
        })

        setStudios(processed as any)
      } catch (err) {
        console.error('❌ GlobalAnalyticsPage: unexpected reload error:', err)
      } finally {
        if (isActive) {
          setIsReloadingPartnerStudios(false)
        }
      }
    }

    let isActive = true
    reloadStudiosForPartnerContext()
    return () => {
      isActive = false
    }
  }, [user?.id, activePartnerNumber, scopedStudios.length, setStudios])
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null>(null)
  const isManagementCompany = useMemo(
    () => scopedStudios.some((studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'),
    [scopedStudios]
  )
  const canViewAnalyticsDecisions = scopedStudios.length > 0
  const analyticsStudios = useMemo(() => {
    let filtered = scopedStudios
    if (isManagementCompany && accessLevel === 'advanced') {
      const visibleStatuses = new Set(['OPEN', 'PRE-OPEN', 'SERVICE'])
      filtered = filtered.filter((studio) => visibleStatuses.has(studio.status_code || 'OPEN'))
    }
    if (cityId != null) {
      filtered = filtered.filter((studio) => studio.city_id === cityId)
    }
    return filtered
  }, [scopedStudios, isManagementCompany, accessLevel, cityId])

  useEffect(() => {
    let active = true

    const loadAccessLevel = async () => {
      if (!isManagementCompany || !user?.id) {
        if (active) {
          setAccessLevel(null)
        }
        return
      }

      const level = await getAccessLevel()
      if (active) {
        setAccessLevel(level)
      }
    }

    loadAccessLevel()

    return () => {
      active = false
    }
  }, [getAccessLevel, isManagementCompany, user?.id])
  
  const [subscriptionType, setSubscriptionType] = useState<StudioSubscriptionType>('MIXED')
  const [showCrmDetails, setShowCrmDetails] = useState(false)
  const {
    chartMode,
    setChartMode,
    conversionWindow,
    setConversionWindow,
    chartView,
    setChartView,
    visibleLines,
    setVisibleLines
  } = useAnalyticsChartsState()

  // Хуки для данных
  const periods = useStudioPeriods()
  
  // Массив ID всех студий
  const studioIds = useMemo(() => analyticsStudios.map(s => s.studio_internal_id), [analyticsStudios])
  // Города выборки решают, какая версия таблицы метрик показывается: переделанная
  // включается, только если ВСЕ студии сводной — из пилотного города (Вологда).
  const analyticsTableCities = useMemo<StudioCityRef[]>(
    () => analyticsStudios.map((studio) => ({ cityId: studio.city_id, cityName: studio.city?.city_name })),
    [analyticsStudios]
  )
  const analyticsStudiosLite = useMemo(
    () =>
      analyticsStudios.map((studio) => ({
        studio_internal_id: studio.studio_internal_id,
        studio_number: studio.studio_number
      })),
    [analyticsStudios]
  )
  const analyticsCurrency = useMemo(
    () => analyticsStudios[0]?.currency_code || 'RUB',
    [analyticsStudios]
  )

  useEffect(() => {
    const loadSubscriptionType = async () => {
      if (studioIds.length === 0) {
        setSubscriptionType('REGULAR')
        return
      }

      try {
        const { data, error } = await (supabase as any)
          .from('studio_subscription_settings')
          .select('studio_internal_id, subscription_type')
          .in('studio_internal_id', studioIds)

        if (!error && Array.isArray(data)) {
          const typeByStudioId = new Map<number, StudioSubscriptionType>(
            data
              .filter((row: any) => row?.studio_internal_id && row?.subscription_type)
              .map((row: any) => [row.studio_internal_id, row.subscription_type as StudioSubscriptionType])
          )

          let hasRegular = false
          let hasSubscription = false

          studioIds.forEach((studioId) => {
            const studioType = typeByStudioId.get(studioId) || 'REGULAR'

            if (studioType === 'REGULAR') {
              hasRegular = true
            }

            if (studioType === 'SUBSCRIPTION') {
              hasSubscription = true
            }

            if (studioType === 'MIXED') {
              hasRegular = true
              hasSubscription = true
            }
          })

          if (hasRegular && hasSubscription) {
            setSubscriptionType('MIXED')
          } else if (hasSubscription) {
            setSubscriptionType('SUBSCRIPTION')
          } else {
            setSubscriptionType('REGULAR')
          }
        } else {
          setSubscriptionType('REGULAR')
        }
      } catch (err) {
        console.error('Error loading global subscription type:', err)
        setSubscriptionType('REGULAR')
      }
    }

    loadSubscriptionType()
  }, [studioIds])
  
  // Загружаем агрегированные данные
  const analytics = useMultiStudioAnalytics({
    studioIds,
    currentPeriod: periods.currentPeriod
  })
  const factorAnalysis = useMultiStudioFactorAnalysis({
    studioIds,
    dailyData: analytics.dailyData,
    currentPeriod: periods.currentPeriod,
    planData: analytics.planData,
    periodMetrics: analytics.analytics?.metrics || null
  })
  
  // Загружаем данные о текущей и предыдущей неделе
  const { currentWeek, prevWeek, loading: weeksLoading } = useMetricWeeks(
    periods.currentPeriod,
    periods.isCurrentPeriod
  )
  
  const metrics = useAnalyticsMetrics({
    dailyData: analytics.dailyData,
    planData: analytics.planData,
    previousPeriodData: analytics.previousPeriodData,
    currentPeriod: periods.currentPeriod,
    totalMetrics: analytics.analytics?.metrics || null,
    currentWeek,
    prevWeek
  })
  
  const { chartData } = useChartData({
    dailyData: analytics.dailyData,
    conversionDailyData: analytics.conversionDailyData,
    planData: analytics.planData,
    conversionWindow
  })

  // Сокращенные ссылки
  const currentPeriod = periods.currentPeriod
  const currentAnalytics = analytics.analytics
  const planData = analytics.planData
  const previousPeriodData = analytics.previousPeriodData
  const currentWeekMetrics = metrics.currentWeekMetrics
  const isCurrentPeriod = periods.isCurrentPeriod
  const {
    weeklyPlans,
    loading: weeklyPlansLoading,
    missingPlanStudios,
    missingPlanStudioDetails,
    zeroRevenueStudios,
    zeroPlanRevenueStudios,
    shouldHideDelta
  } = useGlobalWeeklyPlanDiagnostics({
    studioIds,
    analyticsStudios: analyticsStudiosLite,
    periodId: currentPeriod?.period_id || null,
    planData,
    rawDataReady: analytics.rawDataReady,
    rawWeeklyPlans: analytics.rawWeeklyPlans,
    rawPeriodTotals: analytics.rawPeriodTotals,
    rawAggregatedWeeklyPlans: analytics.rawAggregatedWeeklyPlans
  })

  const handleOpenPlanningForMissingStudio = () => {
    if (activePartnerNumber === null || missingPlanStudioDetails.length === 0) {
      return
    }

    const targetStudio = missingPlanStudioDetails[0]
    navigate(`/partners/${activePartnerNumber}/studios/${targetStudio.studio_internal_id}/studio-metrics-grid`)
  }

  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    availableWeekOptions,
    timeFilterOptions,
    selectedRange
  } = useAnalyticsTimeFilter(currentPeriod)

  const selectedDailyData = useMemo(() => {
    if (!selectedRange) return analytics.dailyData
    return analytics.dailyData.filter((day) => day.metric_date >= selectedRange.start && day.metric_date <= selectedRange.end)
  }, [analytics.dailyData, selectedRange])
  const crmStudioNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          analyticsStudios
            .map((studio) => Number(studio.studio_number))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      ),
    [analyticsStudios]
  )

  const selectedWeekPlanRow = useMemo(() => {
    if (!selectedRange || selectedRange.mode !== 'week' || selectedRange.weekNumber === null) return null
    return weeklyPlans.find((week) => week.week_number_in_period === selectedRange.weekNumber) || null
  }, [selectedRange, weeklyPlans])

  const selectedCardViewMode: 'period' | 'current_week' = selectedRange?.mode === 'week' ? 'current_week' : 'period'

  const selectedWeekForCards = useMemo(() => {
    if (!selectedRange || selectedRange.mode !== 'week') return currentWeek
    const weekNumber = selectedRange.weekNumber || 1
    return {
      week_id: -weekNumber,
      period_id: currentPeriod?.period_id || currentWeek?.period_id || 0,
      week_number_in_period: weekNumber,
      week_start: selectedRange.start,
      week_end: selectedRange.end,
      week_len_days: selectedRange.rangeDays
    }
  }, [selectedRange, currentPeriod?.period_id, currentWeek])

  const selectedWeekMetrics = useMemo<WeekMetrics | null>(() => {
    if (!selectedRange || selectedRange.mode !== 'week') return null
    const totalRevenue = selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0)
    const totalSales = selectedDailyData.reduce(
      (sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0),
      0
    )
    const totalVisitors = selectedDailyData.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0)
    const newSalesAmount = selectedDailyData.reduce(
      (sum, day) => sum + (day.new_sales_amount || 0) + (day.new_subscriptions_amount || 0) + (day.membership_fees_new_amount || 0),
      0
    )
    const newSalesAmountWithoutMembershipFees = selectedDailyData.reduce(
      (sum, day) => sum + (day.new_sales_amount || 0) + (day.new_subscriptions_amount || 0),
      0
    )
    const renewalsAmount = selectedDailyData.reduce(
      (sum, day) => sum + (day.renewals_amount || 0) + (day.subscription_renewals_amount || 0),
      0
    )
    const returningAmount = selectedDailyData.reduce(
      (sum, day) => sum + (day.returning_clients_amount || 0) + (day.subscription_returning_clients_amount || 0),
      0
    )
    const marketingExpenses = selectedDailyData.reduce((sum, day) => sum + (day.marketing_expenses_amount || 0), 0)
    const sortedWeekClients = selectedDailyData
      .filter((day) => day.total_clients_count !== null && day.total_clients_count !== undefined)
      .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
    const weekClientsCount = sortedWeekClients.length > 0
      ? (sortedWeekClients[sortedWeekClients.length - 1].total_clients_count || 0)
      : 0
    const beforeWeek = analytics.dailyData
      .filter((day) => day.metric_date < selectedRange.start && day.total_clients_count !== null && day.total_clients_count !== undefined)
      .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
    const beforeWeekLast = beforeWeek.length > 0 ? beforeWeek[beforeWeek.length - 1] : undefined
    const weekClientsGrowth = beforeWeekLast
      ? weekClientsCount - (beforeWeekLast.total_clients_count || 0)
      : undefined
    return {
      total_revenue: totalRevenue,
      new_sales_amount: newSalesAmount,
      renewals_amount: renewalsAmount,
      returning_clients_amount: returningAmount,
      total_visitors: totalVisitors,
      total_sales: totalSales,
      total_clients_count: weekClientsCount,
      clients_growth: weekClientsGrowth,
      marketing_expenses: marketingExpenses,
      avg_sale_amount: totalSales > 0 ? newSalesAmountWithoutMembershipFees / totalSales : 0,
      conversion_rate: totalVisitors > 0 ? (totalSales / totalVisitors) * 100 : 0,
      days_with_data: selectedDailyData.length,
      week_len_days: selectedRange.rangeDays
    }
  }, [selectedRange, selectedDailyData, analytics.dailyData])

  const selectedWeeklyPlanData = useMemo(() => {
    if (!selectedWeekPlanRow) return null
    return {
      weekly_leads_plan: selectedWeekPlanRow.weekly_leads_plan || 0,
      weekly_appointments_plan: selectedWeekPlanRow.weekly_appointments_plan || 0,
      weekly_new_visitors_plan: selectedWeekPlanRow.weekly_new_visitors_plan || 0,
      weekly_new_sales_plan: (selectedWeekPlanRow.weekly_new_sales_plan || 0) + (selectedWeekPlanRow.weekly_new_subscriptions_count_plan || 0),
      weekly_new_sales_amount_plan: selectedWeekPlanRow.weekly_new_sales_amount_plan || 0,
      weekly_renewals_amount_plan: selectedWeekPlanRow.weekly_renewals_amount_plan || 0,
      weekly_returning_clients_amount_plan: selectedWeekPlanRow.weekly_returning_clients_amount_plan || 0,
      weekly_upgrades_amount_plan: selectedWeekPlanRow.weekly_upgrades_amount_plan || 0,
      weekly_gross_revenue_plan: selectedWeekPlanRow.weekly_gross_revenue_plan || 0
    }
  }, [selectedWeekPlanRow])
  const todayIso = getLocalIsoDate()
  const selectedClientsAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'total_clients_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      fallbackCurrentValue: currentAnalytics?.metrics.total_clients_count,
      fallbackGrowth: currentAnalytics?.metrics.clients_growth,
      allowGrowthFallback: !selectedRange || selectedRange.mode !== 'week'
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, analytics.conversionDailyData, currentAnalytics?.metrics.total_clients_count, currentAnalytics?.metrics.clients_growth])
  const selectedClientsAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'total_clients_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, analytics.conversionDailyData])
  const selectedSubscribersAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'active_subscribers_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, analytics.conversionDailyData])
  const selectedSubscribersAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'active_subscribers_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, analytics.conversionDailyData])
  const selectedVisitorsAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'active_visitors_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, analytics.conversionDailyData])
  const selectedVisitorsAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: analytics.conversionDailyData,
      key: 'active_visitors_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, analytics.conversionDailyData])
  const selectedClientsCount = selectedClientsAtEnd
  const selectedSubscribersCount = selectedSubscribersAtEnd
  const selectedVisitorsCount = selectedVisitorsAtEnd
  const selectedClientsGrowth = useMemo(() => {
    if (selectedClientsAtStart === undefined || selectedClientsCount === undefined) return undefined
    return selectedClientsCount - selectedClientsAtStart
  }, [selectedClientsAtStart, selectedClientsCount])
  const selectedSubscribersGrowth = useMemo(() => {
    if (selectedSubscribersAtStart === undefined || selectedSubscribersCount === undefined) return undefined
    return selectedSubscribersCount - selectedSubscribersAtStart
  }, [selectedSubscribersAtStart, selectedSubscribersCount])
  const selectedVisitorsGrowth = useMemo(() => {
    if (selectedVisitorsAtStart === undefined || selectedVisitorsCount === undefined) return undefined
    return selectedVisitorsCount - selectedVisitorsAtStart
  }, [selectedVisitorsAtStart, selectedVisitorsCount])
  const selectedClientsRetention = useMemo(() => {
    if (selectedClientsAtEnd === undefined) return undefined
    const newClientsCount = selectedDailyData.reduce((sum, day) => sum + (day.new_sales_count || 0), 0)
    const returningClientsCount = selectedDailyData.reduce((sum, day) => sum + (day.returning_clients_count || 0), 0)

    return calculateClientRetention({
      clientsAtStart: selectedClientsAtStart,
      clientsAtEnd: selectedClientsAtEnd,
      newClientsCount,
      returningClientsCount
    })
  }, [selectedDailyData, selectedClientsAtStart, selectedClientsAtEnd, selectedClientsCount])
  const selectedSubscribersRetention = useMemo(() => {
    if (selectedSubscribersAtEnd === undefined) return undefined
    const newSubscribersCount = selectedDailyData.reduce((sum, day) => sum + (day.new_subscriptions_count || 0), 0)
    const upgradedSubscribersCount = selectedDailyData.reduce(
      (sum, day) => sum + (day.subscription_upgrades_count || 0),
      0
    )
    const returningSubscribersCount = selectedDailyData.reduce(
      (sum, day) => sum + (day.subscription_returning_clients_count || 0),
      0
    )

    return calculateClientRetention({
      clientsAtStart: selectedSubscribersAtStart,
      clientsAtEnd: selectedSubscribersAtEnd,
      newClientsCount: newSubscribersCount,
      upgradesCount: upgradedSubscribersCount,
      returningClientsCount: returningSubscribersCount
    })
  }, [selectedDailyData, selectedSubscribersAtStart, selectedSubscribersAtEnd, selectedSubscribersCount])

  const filteredFunnelData = useMemo(() => {
    return {
      leads: selectedDailyData.reduce((sum, day) => sum + (day.leads_count || 0), 0),
      appointments: selectedDailyData.reduce((sum, day) => sum + (day.appointments_count || 0), 0),
      visits: selectedDailyData.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0),
      sales: selectedDailyData.reduce((sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0), 0)
    }
  }, [selectedDailyData])

  const funnelPlanData = useMemo(() => {
    const buildPlan = (leads: number, appointments: number, visits: number, sales: number) => ({
      leads,
      appointments,
      visits,
      sales,
      bookingConversionPlan: leads > 0 ? (appointments / leads) * 100 : 0,
      visitConversionPlan: appointments > 0 ? (visits / appointments) * 100 : 0,
      salesConversionPlan: visits > 0 ? (sales / visits) * 100 : 0
    })

    if (!selectedRange || !currentPeriod || !planData) {
      return buildPlan(
        planData?.leads_count_plan || 0,
        planData?.appointments_count_plan || 0,
        planData?.new_visitors_count_plan || 0,
        (planData?.new_sales_count_plan || 0) + (planData?.new_subscriptions_count_plan || 0)
      )
    }

    if (selectedRange.mode === 'week' && selectedWeekPlanRow) {
      return buildPlan(
        selectedWeekPlanRow.weekly_leads_plan || 0,
        selectedWeekPlanRow.weekly_appointments_plan || 0,
        selectedWeekPlanRow.weekly_new_visitors_plan || 0,
        (selectedWeekPlanRow.weekly_new_sales_plan || 0) + (selectedWeekPlanRow.weekly_new_subscriptions_count_plan || 0)
      )
    }

    const totalPeriodDays = Math.max(1, currentPeriod.period_len_days || 1)
    const rangeRatio = selectedRange.rangeDays / totalPeriodDays
    return buildPlan(
      Math.round((planData.leads_count_plan || 0) * rangeRatio),
      Math.round((planData.appointments_count_plan || 0) * rangeRatio),
      Math.round((planData.new_visitors_count_plan || 0) * rangeRatio),
      Math.round(((planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)) * rangeRatio)
    )
  }, [selectedRange, currentPeriod, planData, selectedWeekPlanRow])

  const funnelDailyBreakdown = useMemo(
    () => selectedDailyData.map((day) => ({
      date: day.metric_date,
      leads: day.leads_count || 0,
      appointments: day.appointments_count || 0,
      visits: day.new_visitors_count || 0,
      sales: (day.new_sales_count || 0) + (day.new_subscriptions_count || 0)
    })),
    [selectedDailyData]
  )

  const funnelTrendData = useMemo(
    () => selectedDailyData.map((day) => ({
      date: day.metric_date,
      leads: day.leads_count || 0,
      appointments: day.appointments_count || 0,
      visits: day.new_visitors_count || 0,
      sales: (day.new_sales_count || 0) + (day.new_subscriptions_count || 0)
    })),
    [selectedDailyData]
  )

  const {
    recurringClientsAnalytics,
    newSalesAnalytics,
    autoRenewalsAnalytics,
    recurringSalesBreakdown,
    newSalesBreakdown,
    recurringSalesDetails,
    refundsAmount,
    recurringShareWithoutRefundsPct,
    newSalesShareWithoutRefundsPct,
    autoRenewalsShareWithoutRefundsPct,
    refundsSharePct
  } = useSalesMixAnalytics({
    selectedRange: selectedRange
      ? { mode: selectedRange.mode, start: selectedRange.start, end: selectedRange.end }
      : null,
    selectedDailyData,
    selectedWeekPlanRow,
    planData
  })
  const {
    crmLoading,
    crmError,
    crmLastSyncAt,
    crmTotals,
    crmPaymentChannels,
    crmCoverage
  } = useCrmRevenueComparison({
    crmStudioNumber: null,
    crmStudioNumbers,
    selectedRange,
    dailyData: analytics.dailyData
  })

  // Константы для легенд графиков
  const volumeLegendItems = [
    { key: 'leads', label: 'Заявки', color: '#FF9800' },
    { key: 'appointments', label: 'Записи', color: '#9C27B0' },
    { key: 'visitors', label: 'Приходы', color: '#2196F3' },
    { key: 'sales', label: 'Продажи', color: '#E91E63' }
  ]

  const conversionLegendItems = [
    { key: 'cr1', label: 'Заявка → Запись', color: '#FF9800' },
    { key: 'cr2', label: 'Запись → Приход', color: '#9C27B0' },
    { key: 'cr3', label: 'Приход → Продажа', color: '#2196F3' },
    { key: 'crTotal', label: 'Общая конверсия', color: '#4CAF50' }
  ]

  // Если студий нет
  if (analyticsStudios.length === 0) {
    const shouldWaitForStudios =
      activePartnerNumber !== null &&
      (studios.length === 0 || isReloadingPartnerStudios)

    if (shouldWaitForStudios) {
      return <LoadingScreen />
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-6">
          <p className="text-muted-foreground">У вас пока нет студий</p>
        </Card>
      </div>
    )
  }

  // Отображение состояния загрузки
  if (analytics.loading || periods.loading) {
    return <LoadingScreen />
  }

  if (analytics.error || periods.error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-6">
          <p className="text-error">{analytics.error || periods.error}</p>
        </Card>
      </div>
    )
  }

  if (!currentAnalytics || !currentPeriod) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-6">
          <p className="text-error">Нет данных для отображения</p>
        </Card>
      </div>
    )
  }

  return (
    <AnalyticsDashboardLayout containerClassName="max-w-none mx-0 space-y-3">
        {/* Period Selector */}
        <PeriodSelector
          currentPeriod={currentPeriod}
          onPrevPeriod={periods.goToPrevPeriod}
          onNextPeriod={periods.goToNextPeriod}
          canGoPrev={periods.canGoPrev}
          canGoNext={periods.canGoNext}
          showViewModeSelector={true}
          isCurrentPeriod={isCurrentPeriod}
          periodFilterValue={selectedTimeFilter}
          periodFilterOptions={timeFilterOptions}
          onPeriodFilterChange={setSelectedTimeFilter}
          selectedRange={selectedRange}
        />

        {/* Key Metrics */}
        <AnalyticsKpiGrid
          mainMetrics={{
            viewMode: selectedCardViewMode,
            periodMetrics: currentAnalytics.metrics,
            weekMetrics: selectedCardViewMode === 'current_week' ? selectedWeekMetrics : currentWeekMetrics,
            planData,
            weeklyPlanData: selectedWeeklyPlanData,
            currentWeek: selectedWeekForCards,
            currentPeriod,
            periodDaysCount: selectedDailyData.length || 1,
            crmComparison: {
              appRevenue: selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0),
              crmRevenue: crmTotals.crmRevenue
            },
            onOpenCrmDetails: () => setShowCrmDetails(true),
            currency: analyticsCurrency
          }}
          funnelData={filteredFunnelData}
          planConversions={{
            cr1Plan: funnelPlanData.bookingConversionPlan || 0,
            cr2Plan: funnelPlanData.visitConversionPlan || 0,
            cr3Plan: funnelPlanData.salesConversionPlan || 0
          }}
          clientsTitle="Клиенты студий"
          clientsCount={selectedClientsCount}
          clientsGrowth={selectedClientsGrowth}
          clientsRetention={selectedClientsRetention}
          subscribersCount={selectedSubscribersCount}
          subscribersGrowth={selectedSubscribersGrowth}
          subscribersRetention={selectedSubscribersRetention}
          visitorsCount={selectedVisitorsCount}
          visitorsGrowth={selectedVisitorsGrowth}
          dailyData={selectedDailyData}
          subscriptionType={subscriptionType}
          newSales={newSalesAnalytics}
          recurringSales={recurringClientsAnalytics}
          autoRenewals={autoRenewalsAnalytics}
          newSalesBreakdown={newSalesBreakdown}
          recurringSalesBreakdown={recurringSalesBreakdown}
          newSalesSharePct={newSalesShareWithoutRefundsPct}
          recurringSharePct={recurringShareWithoutRefundsPct}
          autoRenewalsSharePct={autoRenewalsShareWithoutRefundsPct}
          refundsAmount={refundsAmount}
          refundsSharePct={refundsSharePct}
          missingPlanWarning={{
            studioNumbers: missingPlanStudios,
            onCreateZeroPlan: handleOpenPlanningForMissingStudio
          }}
          currency={analyticsCurrency}
        />

        {/* План - Факт */}
        {planData && (
          <PlanVsFactSection
            planData={planData}
            marketingBudgetFact={currentAnalytics.metrics.marketing_expenses || 0}
            previousPeriodData={previousPeriodData}
            currency={analyticsCurrency}
          />
        )}

        {/* Sales Funnel */}
        <SalesFunnel
          leads={filteredFunnelData.leads}
          appointments={filteredFunnelData.appointments}
          visits={filteredFunnelData.visits}
          sales={filteredFunnelData.sales}
          leadsplan={funnelPlanData.leads}
          appointmentsPlan={funnelPlanData.appointments}
          visitsPlan={funnelPlanData.visits}
          salesPlan={funnelPlanData.sales}
          bookingConversionPlan={funnelPlanData.bookingConversionPlan}
          visitConversionPlan={funnelPlanData.visitConversionPlan}
          salesConversionPlan={funnelPlanData.salesConversionPlan}
          dailyBreakdown={funnelDailyBreakdown}
          trendData={funnelTrendData}
          filterValue={selectedTimeFilter}
          onFilterChange={setSelectedTimeFilter}
          availableWeeks={availableWeekOptions}
          periodStartDate={selectedRange?.start}
          periodEndDate={selectedRange?.end}
          factorAnalysisData={factorAnalysis.data}
          factorAnalysisVariants={factorAnalysis.variants}
          allowFactorSplitViews={factorAnalysis.canShowSplitViews}
          subscriptionType={subscriptionType}
          recurringSalesDetails={recurringSalesDetails}
          todayDate={getLocalIsoDate()}
          currency={analyticsCurrency}
        />

        <AnalyticsChartsSection
          chartData={chartData}
          chartMode={chartMode}
          chartView={chartView}
          conversionWindow={conversionWindow}
          visibleLines={visibleLines}
          planData={planData}
          currency={analyticsCurrency}
          volumeLegendItems={volumeLegendItems}
          conversionLegendItems={conversionLegendItems}
          onChartModeChange={setChartMode}
          onChartViewChange={setChartView}
          onConversionWindowChange={setConversionWindow}
          onToggleVisibleLine={(key) => setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }))}
        />

        <DailyPeriodMetricsTable
          dailyData={analytics.dailyData}
          planData={planData}
          weeklyPlans={weeklyPlans}
          cities={analyticsTableCities}
          studioIds={studioIds}
          canViewDecisions={canViewAnalyticsDecisions}
          missingPlanStudios={missingPlanStudios}
          zeroRevenueStudios={zeroRevenueStudios}
          zeroPlanRevenueStudios={zeroPlanRevenueStudios}
          hideDelta={shouldHideDelta}
          hideDeltaStudioCount={analyticsStudios.length}
          currency={analyticsCurrency}
          selectedRangeMode={selectedRange?.mode || 'period'}
          selectedRangeWeekNumber={selectedRange?.weekNumber || null}
          onSelectedWeekNumberChange={(weekNumber) => setSelectedTimeFilter(`week_${weekNumber}`)}
        />

        <CrmComparisonModal
          isOpen={showCrmDetails && !!selectedRange}
          rangeLabel={selectedRange?.label || 'период'}
          totals={crmTotals}
          paymentChannels={crmPaymentChannels}
          coverage={crmCoverage}
          loading={crmLoading}
          error={crmError}
          lastSyncAt={crmLastSyncAt}
          currency={analyticsCurrency}
          showSubscriptionRevenue={subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED'}
          onClose={() => setShowCrmDetails(false)}
        />

        {/* Bottom padding */}
        <div className="h-20"></div>
    </AnalyticsDashboardLayout>
  )
}
